/**
 * Plan 140: Quick Actions API client.
 */
import { apiClient } from './client';

export type QuickActionStructured = 'red_flags' | 'gap_analysis';
export type QuickActionScope = 'clause' | 'document';
// Plan 188 / Issue #795: declared invocation mode for telemetry. The backend
// can't distinguish a section fan-out from independent per-clause clicks
// without an explicit hint (section-mode is a frontend-only concept).
export type QuickActionInvocationMode = 'per_clause' | 'section';

export interface QuickActionFinding {
  id: string;
  issue_type: string;
}

export interface QuickActionResponse {
  // Issue #771 follow-up: nullable when the LLM returned 0 findings.
  // The synthetic analysis_runs row is created lazily (only when there's
  // something to persist), so no findings -> no run id.
  analysis_run_id: string | null;
  analysis_id: string | null;
  findings: QuickActionFinding[];
}

// Plan 191 / Issue #796 — section-mode quick action response. Extends
// QuickActionResponse with per_clause_outcomes so the frontend can
// reconstruct the existing classifySectionOutcome banner without keeping
// per-clause-call results client-side.
export type SectionPerClauseOutcome =
  | { clause_id: string; kind: 'ok'; message: string | null }
  | { clause_id: string; kind: 'empty'; message: string | null }
  | { clause_id: string; kind: 'err'; message: string | null };

export interface SectionReviewResponse extends QuickActionResponse {
  per_clause_outcomes: SectionPerClauseOutcome[];
}

// LLM-backed calls: single-clause red flags / gap analysis commonly run
// 20-60s; doc-scope parallel fan-out can push several minutes. The default
// 15s axios timeout from client.ts aborts these before the model returns.
const CLAUSE_QUICK_ACTION_TIMEOUT_MS = 120_000;
const DOC_QUICK_ACTION_TIMEOUT_MS = 300_000;

import type { ComparisonFindingWithRunId } from '../../components/desk/utils/compareFindingContext';

// Plan 178: clause-pair compare response is discriminated by `status`.
export type ComparePairResponse =
  | {
      status: 'finding';
      finding_id: string;
      review_document_id: string;
      base_document_id: string;
      analysis_run_id: string;
      /**
       * Plan 235 / #967: the complete, DECRYPTED finding, serialised
       * identically to the document-union endpoint so the shared adapter
       * receives one shape whatever produced the row.
       *
       * OPTIONAL on the wire even though the backend always sends it: an
       * old frontend must tolerate a new backend, and a backend rollback
       * must not crash a new frontend. Callers MUST check at runtime
       * before use -- see ClausePairCompareView.
       */
      finding?: ComparisonFindingWithRunId;
    }
  | {
      status: 'no_substantive_change';
      review_document_id: string;
      base_document_id: string;
      summary: string;
    };

export const quickActionsApi = {
  clauseReview: (
    matterId: string,
    payload: {
      clause_id: string;
      action: QuickActionStructured;
      invocation_mode?: QuickActionInvocationMode;
    },
  ): Promise<QuickActionResponse> =>
    apiClient
      .post<QuickActionResponse>(
        `/api/v1/matters/${matterId}/quick-actions/clause-review`,
        payload,
        { timeout: CLAUSE_QUICK_ACTION_TIMEOUT_MS },
      )
      .then((r) => r.data),

  docReview: (
    matterId: string,
    payload: {
      document_id: string;
      action: QuickActionStructured;
      scope?: QuickActionScope;
    },
  ): Promise<QuickActionResponse> =>
    apiClient
      .post<QuickActionResponse>(
        `/api/v1/matters/${matterId}/quick-actions/doc-review`,
        payload,
        { timeout: DOC_QUICK_ACTION_TIMEOUT_MS },
      )
      .then((r) => r.data),

  comparePair: (
    matterId: string,
    payload: { review_clause_id: string; base_clause_id: string },
  ): Promise<ComparePairResponse> =>
    apiClient
      .post<ComparePairResponse>(
        `/api/v1/matters/${matterId}/quick-actions/clause-compare-pair`,
        payload,
        { timeout: CLAUSE_QUICK_ACTION_TIMEOUT_MS },
      )
      .then((r) => r.data),

  // Plan 191 / Issue #796 — section-mode (N parallel per-clause + 1
  // synthesis call). Uses the doc-action timeout because the orchestration
  // can take longer than a single-clause call.
  sectionReview: (
    matterId: string,
    payload: {
      document_id: string;
      clause_ids: string[];   // COMPOUND clause_ids
      action: QuickActionStructured;
    },
  ): Promise<SectionReviewResponse> =>
    apiClient
      .post<SectionReviewResponse>(
        `/api/v1/matters/${matterId}/quick-actions/section-review`,
        payload,
        { timeout: DOC_QUICK_ACTION_TIMEOUT_MS },
      )
      .then((r) => r.data),
};
